import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { Observable, catchError, map, of, shareReplay } from 'rxjs';

import {
  AemNavigationResponse,
  EditorialResponseLink,
  FooterLinksResponse,
  GlobalBanner,
  GlobalBannerResponse,
  HeaderLinksResponse
} from '@hdms/shared/page-frame/types';
import { EnvironmentService, FeatureToggleService, Logger } from '@hdms/shared/util-core';

import { AEM_NAVIGATION_ENDPOINT, AEM_REQUEST_OPTIONS, AemEndpoint, getAemEndpointUrl } from './aem-endpoint.helper';

const FT_AEM_ENDPOINTS = 'ldt_ngc_unify_aem_endpoints';

@Injectable({ providedIn: 'root' })
export class AemNavigationService {
  private readonly httpClient = inject(HttpClient);
  private readonly envService = inject(EnvironmentService);
  private readonly useCombinedEndpoint = inject(FeatureToggleService).getToggleById(FT_AEM_ENDPOINTS);

  private readonly allNavigationData$ = this.getAllNavigationData$();

  public get editorialLinks$(): Observable<EditorialResponseLink[]> {
    return (
      this.useCombinedEndpoint
        ? this.allNavigationData$
        : this.httpClient
            .get<HeaderLinksResponse>(
              getAemEndpointUrl(AemEndpoint.EDITORIAL_LINKS, this.envService),
              AEM_REQUEST_OPTIONS
            )
            .pipe(
              catchError((error: HttpErrorResponse) => {
                Logger.server.error('Error retrieving editorial links', error.message);
                return of({ editorialNavigation: [] });
              })
            )
    ).pipe(map(data => data.editorialNavigation));
  }

  public get footerLinks$(): Observable<FooterLinksResponse> {
    return this.useCombinedEndpoint
      ? this.allNavigationData$.pipe(map(data => data.footerNavigation))
      : this.httpClient
          .get<FooterLinksResponse>(getAemEndpointUrl(AemEndpoint.FOOTER_LINKS, this.envService), AEM_REQUEST_OPTIONS)
          .pipe(
            catchError((error: HttpErrorResponse) => {
              Logger.server.error('Error retrieving footer links', error.message);
              return of(null);
            })
          );
  }

  public get globalBanner$(): Observable<GlobalBanner> {
    return (
      this.useCombinedEndpoint
        ? this.allNavigationData$
        : this.httpClient
            .get<GlobalBannerResponse>(
              getAemEndpointUrl(AemEndpoint.GLOBAL_BANNER, this.envService),
              AEM_REQUEST_OPTIONS
            )
            .pipe(
              catchError((error: HttpErrorResponse) => {
                Logger.server.error('Error retrieving global banner data', error.message);
                return of({ globalBanner: null });
              })
            )
    ).pipe(map(({ globalBanner }) => globalBanner?.text && globalBanner));
  }

  private getAllNavigationData$(): Observable<AemNavigationResponse> {
    return this.httpClient
      .get<AemNavigationResponse>(getAemEndpointUrl(AEM_NAVIGATION_ENDPOINT, this.envService), AEM_REQUEST_OPTIONS)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          Logger.server.error('Error retrieving AEM navigation links', error.message);
          return of<AemNavigationResponse>({
            editorialNavigation: [],
            footerNavigation: null,
            globalBanner: null
          });
        }),
        shareReplay(1)
      );
  }
}
